import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ReactComponent as HowItWorksCalendarSVG } from '../../assets/images/home/how-it-works-calendar.svg';
import { ReactComponent as HowItWorksFastForwardSVG } from '../../assets/images/home/how-it-works-fast-forward.svg';
import { ReactComponent as HowItWorksInboxSVG } from '../../assets/images/home/how-it-works-inbox.svg';
import { ReactComponent as HowItWorksShareSVG } from '../../assets/images/home/how-it-works-share.svg';
import { ReactComponent as HowItWorksUserPlusSVG } from '../../assets/images/home/how-it-works-user-plus.svg';
import { ReactComponent as HowItWorksZapSVG } from '../../assets/images/home/how-it-works-zap.svg';
import { ReactComponent as QuotesSVG } from '../../assets/images/home/quotes.svg';
import SMSModal from '../../components/other/SMSModal';
import { useAuth } from '../../container/authContext';
import { usePermission } from '../../container/permissionContext';
import Footer from './components/Footer';
import Header from './components/Header/Header';
import PreFooterBlock from './components/PreFooterBlock';
import './styles.scss';

const howItWorksSteps = [
  {
    iconComponent: <HowItWorksCalendarSVG />,
    title: 'Schedule presentation',
  },
  {
    iconComponent: <HowItWorksInboxSVG />,
    title: 'We collect samples of your current forms and related documentation processes',
  },
  {
    iconComponent: <HowItWorksShareSVG />,
    title: 'We convert your forms in to digital editions and make them follow your documentation process.',
  },
  {
    iconComponent: <HowItWorksUserPlusSVG />,
    title: 'We help you to enroll employees into the platform — takes about 5 minutes to enroll 5 employees.',
  },
  {
    iconComponent: <HowItWorksFastForwardSVG />,
    title: 'The transition from paper overload to digital efficiencies begins and progresses one form/process at a time',
  },
  {
    iconComponent: <HowItWorksZapSVG />,
    title: 'After about 4 months, your organization is a paper-free operation.',
  },
];

const operationSlides = [
  {
    imgSrc: '/images/home/operation-1.png',
    title: 'Operational Logs & Documentation',
    subtitle: '',
  },
  {
    imgSrc: '/images/home/operation-2.jpg',
    title: 'CNA Assignments',
    subtitle: 'Fully customized by charge nurses for every unit and shift. Automatically archived and always accessible.',
  },
  {
    imgSrc: '/images/home/home-operation-3.jpg',
    title: 'Family Communication for Every Occasion',
    subtitle: '',
  },
];

function HomePage() {
  const { user } = useAuth();
  const swiperRef = useRef(null);
  const operationsRef = useRef(null);
  const navigation = useNavigate();
  const { isSuperAdmin } = usePermission();
  const { is_unit_user = false } = user || {};
  const [isOpenScheduleDemoModal, setIsOpenScheduleDemoModal] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const handelClose = () => {
    setIsOpen(false);
  };

  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (user) {
      if (isSuperAdmin()) {
        if (user?.company?.id) {
          navigation(`company/${user.company.id}`);
        } else {
          navigation('/companies');
        }
      } else {
        if (is_unit_user) {
          navigation('/cna/assignments');
        } else {
          navigation('/corporate-library/');
        }
      }
    }
  }, [user, isSuperAdmin, navigation, is_unit_user]);

  if (user) {
    return;
  }

  return (
    <main className={cx('home-page', { isMobile })}>
      <Header isOpenScheduleDemoModal={isOpenScheduleDemoModal} setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal} />
      <div className={cx('home-page-content', { isMobile })}>
        <div className="home-page-hero">
          <div className="left-block">
            <div className="home-page-hero-title">
              {isMobile ? (
                <>
                  <span className="blue-part">Completely</span> Paperless
                  <br />
                  Operations & Compliance <br /> <span className="blue-part">for Senior Communities</span>
                </>
              ) : (
                <>
                  Completely Paperless
                  <br />
                  Operations & Compliance <br /> <span className="blue-part">for Senior Communities</span>
                </>
              )}
            </div>
            <div className="home-page-hero-subtitle">
              Customized solutions for every operational challenge <br /> — from education to audits.
            </div>
            <button className="hero-request-demo-btn" onClick={() => setIsOpenScheduleDemoModal(true)}>
              Request a Demo{' '}
              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.75 9.89771H14.25" stroke="#448AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M9 4.64771L14.25 9.89771L9 15.1477"
                  stroke="#448AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="right-block">
            <img src="/images/home/devices.png" />
          </div>
        </div>
        <div className="problem-solution">
          {!isMobile && (
            <div className="paper-wrapper">
              <img src="/images/home/paper.png" />
            </div>
          )}
          <div className="problem-solution-center-block">
            <div className="problem-solution-arrow-wrapper">
              {isMobile ? (
                <img src="/images/home/home-paperless.png" />
              ) : (
                <svg width="486" height="105" viewBox="0 0 486 105" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0.583571 101.384C-0.196049 102.166 -0.193735 103.433 0.588739 104.212C1.37121 104.992 2.63754 104.99 3.41716 104.207L0.583571 101.384ZM112.361 28.7846L111.594 26.9378L112.361 28.7846ZM242.541 2.79541V0.79541V2.79541ZM372.721 28.7845L371.953 30.6313L372.721 28.7845ZM483.078 104.795C484.182 104.797 485.079 103.904 485.081 102.799L485.114 84.799C485.116 83.6944 484.223 82.7973 483.118 82.7953C482.013 82.7933 481.116 83.6871 481.114 84.7917L481.085 100.792L465.085 100.762C463.981 100.76 463.084 101.654 463.082 102.759C463.079 103.863 463.973 104.76 465.078 104.762L483.078 104.795ZM2.00037 102.795L3.41716 104.207C34.82 72.6892 72.1002 47.6883 113.129 30.6313L112.361 28.7846L111.594 26.9378C70.0783 44.1969 32.3572 69.4938 0.583571 101.384L2.00037 102.795ZM112.361 28.7846L113.129 30.6313C154.158 13.5744 198.132 4.79542 242.541 4.79541V2.79541V0.79541C197.605 0.79542 153.109 9.67868 111.594 26.9378L112.361 28.7846ZM242.541 2.79541V4.79541C286.95 4.7954 330.924 13.5744 371.953 30.6313L372.721 28.7845L373.488 26.9377C331.973 9.67864 287.477 0.7954 242.541 0.79541V2.79541ZM372.721 28.7845L371.953 30.6313C412.982 47.6882 450.262 72.6891 481.665 104.207L483.081 102.795L484.498 101.384C452.725 69.4937 415.003 44.1969 373.488 26.9377L372.721 28.7845Z"
                    fill="url(#paint0_linear_2195_3604)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2195_3604"
                      x1="2.00037"
                      y1="52.7954"
                      x2="483.082"
                      y2="52.7954"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#ECEEF1" />
                      <stop offset="0.5" stopColor="#98A2B3" />
                      <stop offset="1" stopColor="#3571D6" />
                    </linearGradient>
                  </defs>
                </svg>
              )}
            </div>
            <div className="problem-solution-text">
              Transform Compliance and Оperations into a completely paperless, <span className="blue-part">digital process.</span>
            </div>
            <Link to="/how-it-works" className="how-it-works-btn">
              How it Works{' '}
              <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.25 9.79541H14.75" stroke="#448AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M9.5 4.54541L14.75 9.79541L9.5 15.0454"
                  stroke="#448AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
          {!isMobile && (
            <div className="problem-solution-right-block">
              <img src="/images/home/cna.jpg" />
            </div>
          )}
        </div>

        <div className="staff-education">
          <div className="staff-education-left-block">
            <img src="/images/home/education_forms.png" />
          </div>
          <div className="staff-education-right-block">
            <div>
              <div className="staff-education-chip">Staff Education Delivery</div>
            </div>
            <p className="staff-education-subtitle">
              · Most efficient way to deliver and track staff education
              <br /> · Easy to achieve high completion rates
              <br />· Generate survey friendly reports <br />· Create custom education content & videos
            </p>
          </div>
        </div>

        <div className="compliance-block">
          <div className="compliance-left-block">
            <div>
              <div className="compliance-chip">Compliance & Audit Tools</div>
            </div>

            <p className="compliance-subtitle">
              Complete and keep all your compliance documentation in one place, always accessible to staff and management
            </p>
          </div>
          <div className="compliance-right-block">
            <img src="/images/home/med-card.png" />
          </div>
        </div>

        <div className="operations-block">
          <h3 className="operations-title">Solutions to Operational Challenges</h3>
          {isMobile ? (
            <div className="operations-slider">
              <Swiper
                modules={[Navigation, Pagination]}
                onSwiper={(swiper) => (operationsRef.current = swiper)}
                spaceBetween={20}
                slidesPerView={1}
                loop
                pagination={{ clickable: true }}
              >
                {operationSlides.map((step, index) => (
                  <SwiperSlide key={index}>
                    <div className="operation-slide">
                      <div className="operation-slide-img-wrapper">
                        <img className="operation-slide-img" src={step.imgSrc} />
                      </div>
                      <div className="operation-slide-content">
                        <p className="operation-slide-title">{step.title}</p>
                        <p className="operation-slide-subtitle">{step.subtitle}</p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="custom-nav">
                <button onClick={() => operationsRef.current?.slidePrev()}>
                  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12.7954H5" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path
                      d="M12 19.7954L5 12.7954L12 5.79541"
                      stroke="#3571D6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button onClick={() => operationsRef.current?.slideNext()}>
                  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12.7954H19" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path
                      d="M12 5.79541L19 12.7954L12 19.7954"
                      stroke="#3571D6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div className="operation-items-wrapper">
              <div className="operation-item">
                <div>
                  <img src="/images/home/operation-1.png" />
                </div>
                <div className="operation-item-text-wrapper">
                  <div className="operation-item-title">Operational Logs & Documentation</div>
                </div>
              </div>
              <div className="operation-item">
                <div>
                  <img src="/images/home/operation-2.jpg" />
                </div>
                <div className="operation-item-text-wrapper">
                  <div className="operation-item-title">CNA Assignments</div>
                  <div className="operation-item-subtitle">
                    Fully customized by charge nurses for every unit and shift. Automatically archived and always accessible.
                  </div>
                </div>
              </div>
              <div className="operation-item">
                <div>
                  <img src="/images/home/home-operation-3.jpg" />
                </div>
                <div className="operation-item-text-wrapper">
                  <div className="operation-item-title">Family Communication for Every Occasion</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={cx(`easy-access bg-${currentSlide}`, { isMobile })}>
          <div className="easy-access-slider">
            <div className="slider-container">
              <div className="slider-slide">
                <div className="slide-content">
                  <h3>
                    Ease Your
                    <br /> Documentation Burden
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="core-benefits">
          <h3 className="core-benefits-title">Core Benefits</h3>
          {isMobile ? (
            <div className="core-benefits-mobile-wrapper">
              <div className="core-benefits-row core-benefits-row-1">
                <div className="core-benefits-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-benefits-zap.svg" />
                  </div>
                  <div className="core-benefits-inner-wrapper">
                    <div className="core-benefits-item-content">
                      <h4 className="core-benefits-item-title">Customization</h4>
                      <p className="core-benefits-item-subtitle">Custom solutions for your organization at no extra cost</p>
                    </div>
                    <div className="core-benefits-item-image-wrapper">
                      <img className="core-benefits-item-screenshot-img" src="/images/home/core-benefites-temperature-screenshot.png" />
                    </div>
                  </div>
                </div>
                <div className="core-benefits-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-benefits-user-check.svg" />
                  </div>
                  <div className="core-benefits-inner-wrapper">
                    <div className="core-benefits-item-content">
                      <h4 className="core-benefits-item-title">Streamlined Operations</h4>
                    </div>
                    <div className="core-benefits-item-image-wrapper">
                      <img className="core-benefits-item-screenshot-img" src="/images/home/oversight-screenshot.png" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="core-benefits-row core-benefits-row-2">
                <div className="core-benefits-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-benefits-users.svg" />
                  </div>
                  <div className="core-benefits-inner-wrapper">
                    <div className="core-benefits-item-content">
                      <h4 className="core-benefits-item-title">
                        HR forms and <br />
                        evaluations
                      </h4>
                      <p className="core-benefits-item-subtitle">Onboard staff and manage HR documentation without printing a paper</p>
                    </div>
                    <div className="core-benefits-item-image-wrapper">
                      <img className="core-benefits-item-screenshot-img hr-forms-screenshot" src="/images/home/missed-punch-log.png" />
                    </div>
                  </div>
                </div>
                <div className="core-benefits-cols-wrapper">
                  <div className="core-benefits-col">
                    <div className="corner-image-wrapper">
                      <img className="corner-image" src="/images/home/core-benefits-tv.svg" />
                    </div>
                    <div className="core-benefits-inner-wrapper">
                      <div className="core-benefits-item-content">
                        <h4 className="core-benefits-item-title">One Platform Replacing Multiple Costly Software Programs</h4>
                      </div>
                    </div>
                  </div>
                  <div className="core-benefits-col">
                    <div className="corner-image-wrapper">
                      <img className="corner-image" src="/images/home/core-benefits-pie.svg" />
                    </div>
                    <div className="core-benefits-inner-wrapper">
                      <div className="core-benefits-item-content">
                        <h4 className="core-benefits-item-title">Built in reporting</h4>
                        <p className="core-benefits-item-subtitle"> Summaries and Reports for all completed documentation</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="core-benefits-row core-benefits-row-3">
                <div className="core-benefits-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-benefits-eye.svg" />
                  </div>
                  <div className="core-benefits-inner-wrapper">
                    <div className="core-benefits-item-content">
                      <h4 className="core-benefits-item-title">Regional management has full access to documentation at every facility</h4>
                    </div>
                  </div>
                </div>
                <div className="core-benefits-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-benefits-pie.svg" />
                  </div>
                  <div className="core-benefits-inner-wrapper">
                    <div className="core-benefits-item-content">
                      <h4 className="core-benefits-item-title">Maintenance & safety inspections</h4>
                      <p className="core-benefits-item-subtitle">Get all documents signed without leaving your office</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="core-benefits-row core-benefits-row-4">
                <div className="core-benefits-col">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-benefits-clock.svg" />
                  </div>
                  <div className="core-benefits-inner-wrapper">
                    <div className="core-benefits-item-content">
                      <h4 className="core-benefits-item-title">Policy sign-offs and digital checklists</h4>
                      <p className="core-benefits-item-subtitle">Get all documents signed without leaving your office</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="core-benefits-grid-container">
              <div className="core-benefits-item core-benefits-item-tall">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-benefits-zap.svg" />
                </div>
                <div className="core-benefits-item-content">
                  <h4 className="core-benefits-item-title">Customization</h4>
                  <p className="core-benefits-item-subtitle">Custom solutions for your organization at no extra cost</p>
                </div>
                <div>
                  <img className="core-benefits-item-screenshot-img" src="/images/home/core-benefites-temperature-screenshot.png" />
                </div>
              </div>

              <div className="core-benefits-item core-benefits-item-tall">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-benefits-user-check.svg" />
                </div>
                <div className="core-benefits-item-content">
                  <h4 className="core-benefits-item-title">
                    Streamlined <br />
                    Operations
                  </h4>
                </div>
                <div>
                  <img className="core-benefits-item-screenshot-img" src="/images/home/oversight-screenshot.png" />
                </div>
              </div>

              <div className="core-benefits-item core-benefits-item-tall">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-benefits-users.svg" />
                </div>
                <div className="core-benefits-item-content">
                  <h4 className="core-benefits-item-title">
                    HR forms and <br />
                    evaluations
                  </h4>
                  <p className="core-benefits-item-subtitle">Onboard staff and manage HR documentation without printing a paper</p>
                </div>
                <div>
                  <img
                    className="core-benefits-item-screenshot-img core-benefits-log-screenshot"
                    src="/images/home/core-benefits-log.png"
                  />
                </div>
              </div>

              <div className="core-benefits-item">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-benefits-tv.svg" />
                </div>
                <div className="core-benefits-item-content">
                  <h4 className="core-benefits-item-title">One Platform Replacing Multiple Costly Software Programs</h4>
                </div>
              </div>

              <div className="core-benefits-item">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-benefits-pie.svg" />
                </div>
                <div className="core-benefits-item-content">
                  <h4 className="core-benefits-item-title">Built in reporting</h4>
                  <p className="core-benefits-item-subtitle"> Summaries and Reports for all completed documentation</p>
                </div>
              </div>

              <div className="core-benefits-item">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-benefits-eye.svg" />
                </div>
                <div className="core-benefits-item-content">
                  <h4 className="core-benefits-item-title">Regional management has full access to documentation at every facility</h4>
                </div>
              </div>

              <div className="core-benefits-item core-benefits-item-wide">
                <div className="core-benefits-item-right-block">
                  <div className="corner-image-wrapper">
                    <img className="corner-image" src="/images/home/core-benefits-tool.svg" />
                  </div>
                  <div className="core-benefits-item-content">
                    <h4 className="core-benefits-item-title">Maintenance & safety inspections</h4>
                    <p className="core-benefits-item-subtitle">Take advantage of our fully customized Life & Safety compliance package</p>
                  </div>
                </div>
              </div>

              <div className="core-benefits-item">
                <div className="corner-image-wrapper">
                  <img className="corner-image" src="/images/home/core-benefits-clock.svg" />
                </div>
                <div className="core-benefits-item-content">
                  <h4 className="core-benefits-item-title">Policy sign-offs and digital checklists</h4>
                  <p className="core-benefits-item-subtitle">Get all documents signed without leaving your office</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="how-it-works-block">
          <h3 className="how-it-works-title">How It Works</h3>
          <div className="how-it-works-slider">
            <Swiper
              modules={[Navigation, Pagination]}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              spaceBetween={20}
              slidesPerView={isMobile ? 1 : 1.5}
              loop
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2.5 },
                1024: { slidesPerView: 3.2 },
              }}
            >
              {howItWorksSteps.map((step, index) => (
                <SwiperSlide key={index}>
                  <div className="how-it-works-slide">
                    <div style={{ height: '30px' }}></div>
                    <div className="icon-wrapper">{step.iconComponent}</div>
                    <div className="how-it-works-slide-content">
                      <div className="how-it-works-numeration">{index + 1}</div>
                      <p className="text">{step.title}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="custom-nav">
              <button onClick={() => swiperRef.current?.slidePrev()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12.7954H5" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 19.7954L5 12.7954L12 5.79541"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button onClick={() => swiperRef.current?.slideNext()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12.7954H19" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 5.79541L19 12.7954L12 19.7954"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="reviews-block">
          <div className="review-block-item">
            <div className="quotes-wrapper">
              <QuotesSVG />
            </div>
            <div className="review-text-wrapper">
              AllCareForms is <span className="blue-part">a true problem solver.</span> Our entire team depends on it for productivity and
              compliance. Our managers and staff appreciate the change.
            </div>
            <div className="reviewer-wrapper">
              <div className="reviewer-name-wrapper">
                <p className="reviewer-name">Shonte Allen</p>
                <p className="reviewer-position">The Residence at Arrowood, Pittsburgh PA.</p>
              </div>
            </div>
          </div>
          <div className="review-block-item">
            <div className="quotes-wrapper">
              <QuotesSVG />
            </div>
            <div className="review-text-wrapper">
              Utilizing AllCareForms changed the way we complete all daily tasks.{' '}
              <span className="blue-part">No more digging for documents,</span> chasing signatures and searching for the correct paper forms
            </div>
            <div className="reviewer-wrapper">
              <div className="reviewer-name-wrapper">
                <p className="reviewer-name">Kevin Kolarosky</p>
                <p className="reviewer-position">Administrator, Townview Health and Rehab Center, Canonsburg PA</p>
              </div>
            </div>
          </div>
        </div>

        <PreFooterBlock
          title="Ready to eliminate paper and optimize compliance?"
          subtitle="Customized solutions for every operational challenge — from education to audits."
          setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal}
        />
        <Footer />
      </div>

      <SMSModal isOpen={isOpen} closeModal={handelClose} title={'SMS opt-in consent'}></SMSModal>
    </main>
  );
}

export default HomePage;
